import { useMemo, FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hooks/useStore';
import { useMyQuery } from '@hooks/useMyQuery';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useOnEntityClick } from '@hooks/useOnEntityClick';
import { useFilterEntity } from '@hooks/useFilterEntity';
import Table from '@components/Core/Table/Table';
import { EntityIcon } from '@components/Core/EntityIcon/EntityIcon';
import { StatusBadge } from '@components/Core/StatusBadge';
import Popup from '@components/Core/Popup/Popup';
import { NoResults } from '@components/Core/NoResults';
import { Button } from '@components/Core/Button';
import { AppLoader } from '@components/Core/AppLoader';
import { StatusPopup } from '@popups/StatusPopup';
import SettingsIcon from '@icons/settings_icon.svg?component';
import { PointsTransFields } from '@pages/Points/types';
import { pointsTranslations } from '@pages/Points/translations';
import { IPointsListData } from '@stores/points/types';
import { PointsTableTransFields } from './types';
import { pointsTableTranslations } from './translations';

const columnHelper = createColumnHelper<IPointsListData>();

const PointsTable: FC<{ search: string }> = observer(({ search }) => {
  const {
    pointsStore: { getPointsList, pointsList },
    mainStore: { selectedCountryCode },
    popupStore: { popupName },
  } = useStore();

  const { brandId } = useParams();

  const { isFetching } = useMyQuery({
    queryKey: ['getPointsList'],
    queryFn: () => brandId && getPointsList(brandId),
    enabled: !!selectedCountryCode || !!brandId, // wait until it's defined
    refetchOnMount: 'always',
  });

  const { t } = useMyTranslations<PointsTableTransFields | Extract<PointsTransFields, 'addPoint'>>([
    pointsTableTranslations.name,
    pointsTranslations.name,
  ]);

  const navigate = useNavigate();

  const data = useFilterEntity(pointsList, search, ['name', 'address']);

  const { onClick, popupData } = useOnEntityClick<IPointsListData>();

  const columns: ColumnDef<IPointsListData, any>[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('name'),
        cell: ({
          row: {
            original,
            original: { logoUrl, name },
          },
        }) => (
          <button
            className="flex items-center w-full gap-x-[12px] bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <EntityIcon src={logoUrl} name="point image" />
            <p className="whitespace-nowrap overflow-hidden text-ellipsis">{name || '—'}</p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true },
      }),
      columnHelper.accessor('address', {
        id: 'address',
        header: t('address'),
        cell: ({
          row: {
            original,
            original: { address },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">{address}</p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'hide-on-mobile' },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: t('status'),
        cell: ({
          row: {
            original,
            original: { status },
          },
        }) => (
          <button
            className="w-full h-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <StatusBadge status={status} />
          </button>
        ),
        enableSorting: true,
        meta: { width: 80 },
      }),
      {
        id: 'option',
        header: t('option'),
        cell: ({ row: { original } }) => (
          <button
            className="w-full h-full bg-none cursor-pointer flex-centred"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <SettingsIcon height={24} width={24} />
          </button>
        ),
        enableSorting: false,
        meta: { width: 56 },
      },
    ],
    [isFetching]
  );

  if (isFetching) return <AppLoader />;
  if ((data.length === 0 && search) || pointsList?.length === 0)
    return (
      <NoResults>
        <p className="small-text">{t('noResultText')}</p>
        <Button
          text={t('addPoint', { ns: pointsTranslations.name })}
          onClick={() => navigate(`/brands/${brandId}/add-point`)}
          styleProp="max-w-[153px]"
        />
      </NoResults>
    );
  return (
    <>
      <Table columns={columns} data={data} />
      <Popup>{popupName === 'status' && popupData && <StatusPopup {...popupData} />}</Popup>
    </>
  );
});

export default PointsTable;
